import { Button, Card, Select, Space, Statistic, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type MarkRow = {
  id: string;
  student_id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  stop_code?: string;
  stop_name?: string;
  status: string;
};

type RunPayload = {
  run: {
    id: string;
    route_id: string;
    route_code?: string;
    route_name?: string;
    run_date: string;
    direction: string;
    status: string;
    record_version: number;
  };
  marks: MarkRow[];
  summary: {
    roster_count: number;
    boarded: number;
    absent: number;
    missed_stop: number;
    excused: number;
  };
};

const STATUS_OPTIONS = [
  { value: 'boarded', label: 'Boarded' },
  { value: 'absent', label: 'Absent' },
  { value: 'missed_stop', label: 'Missed stop' },
  { value: 'excused', label: 'Excused' },
];

export default function TransportBoardingRunPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.transport.manage') ||
      user?.permissions?.includes('education.admin')
  );

  const detail = useQuery({
    queryKey: ['education-transport-boarding-run', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await api.get<RunPayload>(`/education/transport/boarding/${id}`);
      return res.data;
    },
  });

  const [marks, setMarks] = useState<Record<string, string>>({});

  useEffect(() => {
    const rows = detail.data?.marks || [];
    const next: Record<string, string> = {};
    for (const row of rows) next[row.student_id] = row.status;
    setMarks(next);
  }, [detail.data]);

  const save = useMutation({
    mutationFn: () =>
      api.post(`/education/transport/boarding/${id}/marks`, {
        marks: Object.entries(marks).map(([student_id, status]) => ({ student_id, status })),
      }),
    onSuccess: () => {
      message.success(t('transportBoardingSaved'));
      queryClient.invalidateQueries({ queryKey: ['education-transport-boarding-run', id] });
      queryClient.invalidateQueries({ queryKey: ['education-transport-boarding'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const complete = useMutation({
    mutationFn: () =>
      api.put(`/education/transport/boarding/${id}`, {
        status: 'completed',
        record_version: detail.data?.run.record_version,
      }),
    onSuccess: () => {
      message.success(t('transportBoardingCompleted'));
      queryClient.invalidateQueries({ queryKey: ['education-transport-boarding-run', id] });
      queryClient.invalidateQueries({ queryKey: ['education-transport-boarding'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const run = detail.data?.run;
  const summary = detail.data?.summary;

  return (
    <EducationPageChrome
      title={t('transportBoardingRun')}
      description={
        run
          ? `${run.route_code || ''} — ${run.route_name || ''} · ${run.run_date} · ${run.direction}`
          : t('transportBoardingIntro')
      }
      extra={
        <Space wrap>
          {canManage && run && run.status !== 'cancelled' && run.status !== 'completed' ? (
            <>
              <Button type="primary" loading={save.isPending} onClick={() => save.mutate()}>
                {t('saveBoarding')}
              </Button>
              <Button loading={complete.isPending} onClick={() => complete.mutate()}>
                {t('completeBoardingRun')}
              </Button>
            </>
          ) : null}
          <Button
            onClick={() =>
              navigate(
                run?.route_id
                  ? `/education/transport/boarding?route_id=${run.route_id}`
                  : '/education/transport/boarding'
              )
            }
          >
            {t('backToBoarding')}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" loading={detail.isLoading}>
          <Space size="large" wrap>
            <Statistic title={t('status')} value={run?.status || '—'} />
            <Statistic title={t('roster')} value={summary?.roster_count ?? 0} />
            <Statistic title={t('boarded')} value={summary?.boarded ?? 0} />
            <Statistic title={t('absent')} value={summary?.absent ?? 0} />
            <Statistic title={t('missedStop')} value={summary?.missed_stop ?? 0} />
            <Statistic title={t('excused')} value={summary?.excused ?? 0} />
          </Space>
        </Card>

        <Card size="small">
          <Table
            rowKey="student_id"
            size="small"
            loading={detail.isLoading}
            dataSource={detail.data?.marks || []}
            pagination={false}
            columns={[
              {
                title: t('student'),
                render: (_: unknown, row: MarkRow) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
              },
              {
                title: t('transportStop'),
                width: 160,
                render: (_: unknown, row: MarkRow) =>
                  row.stop_code || row.stop_name
                    ? `${row.stop_code || ''} ${row.stop_name || ''}`.trim()
                    : '—',
              },
              {
                title: t('boarding'),
                width: 180,
                render: (_: unknown, row: MarkRow) =>
                  canManage && run?.status !== 'cancelled' ? (
                    <Select
                      style={{ width: 160 }}
                      value={marks[row.student_id] || row.status}
                      options={STATUS_OPTIONS}
                      onChange={(value) =>
                        setMarks((prev) => ({ ...prev, [row.student_id]: value }))
                      }
                    />
                  ) : (
                    <Tag>{row.status}</Tag>
                  ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
